import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Tooltip,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { adminUsersAPI } from '../../services/api';
import { getCountries } from '../../data/locationData';
import { useAuth } from '../../contexts/AuthContext';

function keepsAssignedCountries(role) {
  return role === 'parent_feedback_agent' || role === 'sales' || role === 'sales_supervisor' || role === 'country_manager';
}

function keepsDemoProfile(role) {
  return role === 'sales' || role === 'sales_supervisor' || role === 'country_manager';
}

const TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Australia/Brisbane',
  'Pacific/Auckland',
];

const TIMEZONE_LABELS = {
  'Australia/Brisbane': 'Brisbane, Queensland (AEST, UTC+10:00)',
};

const STUDENT_ROLE_MESSAGE =
  'Students and parents cannot be created or assigned from User Management. Add the student from the Students menu.';
const FAMILY_ROLES_HIDDEN_FROM_ASSIGN = new Set(['student', 'parent']);

function clientLoginEmailIssue(currentEmail, nextRaw, confirmRaw) {
  const next = String(nextRaw || '').trim().toLowerCase();
  const again = String(confirmRaw || '').trim().toLowerCase();
  if (!next) return 'Enter the new login email.';
  if (next !== again) return 'The two email addresses do not match.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) return 'Enter a valid email address.';
  if (next === String(currentEmail || '').trim().toLowerCase()) return 'This is already the login email.';
  if (next.startsWith('temp_') && next.endsWith('@example.com')) return 'Use a real email address.';
  return '';
}

const UserManagement = () => {
  const queryClient = useQueryClient();
  const { isAdmin, user: actor, updateUser } = useAuth();
  const canManageUsersFully = typeof isAdmin === 'function' ? isAdmin() : false;
  const [filters, setFilters] = useState({
    role: '',
    search: '',
    isActive: '',
    page: 1,
    limit: 20,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    timezone: 'UTC',
    role: 'teacher',
    globalTeachingOps: false,
    assignedCountries: [],
    demoReady: 'No',
    demoCountries: [],
  });
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    timezone: 'UTC',
    isActive: true,
    role: '',
    globalTeachingOps: false,
    assignedCountries: [],
    demoReady: 'No',
    demoCountries: [],
  });
  const [resetForm, setResetForm] = useState({ newPassword: '', confirm: '' });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ next: '', confirm: '' });
  const [emailStep, setEmailStep] = useState('edit');
  const [emailError, setEmailError] = useState('');
  const [emailNotices, setEmailNotices] = useState([]);
  const [emailBusy, setEmailBusy] = useState(false);

  const countryList = getCountries();

  const { data, isLoading } = useQuery(
    ['adminUsers', filters],
    async () => {
      const params = {
        page: filters.page,
        limit: filters.limit,
        role: filters.role || undefined,
        search: filters.search || undefined,
        isActive: filters.isActive === '' ? undefined : filters.isActive === 'true',
      };
      const res = await adminUsersAPI.list(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

  const rolesQuery = useQuery(
    ['adminUsersRoles'],
    async () => {
      const res = await adminUsersAPI.getRoles();
      return res.data;
    }
  );
  const roles = (rolesQuery.data?.data || []).filter((r) => r !== 'parent');
  const staffAssignableRoles = roles.filter((r) => !FAMILY_ROLES_HIDDEN_FROM_ASSIGN.has(r));
  const roleOptionsForUser = (userRole) =>
    userRole === 'student' ? ['student', ...staffAssignableRoles] : staffAssignableRoles;

  const createMutation = useMutation(
    (payload) => adminUsersAPI.create(payload),
    {
      onSuccess: () => {
        toast.success('User created');
        queryClient.invalidateQueries('adminUsers');
        setCreateOpen(false);
        setCreateForm({
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          phone: '',
          country: '',
          timezone: 'UTC',
          role: 'teacher',
          globalTeachingOps: false,
          assignedCountries: [],
        });
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to create user');
        toast.error(msg);
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => adminUsersAPI.update(id, payload),
    {
      onSuccess: () => {
        toast.success('User updated');
        queryClient.invalidateQueries('adminUsers');
        setEditOpen(false);
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to update user');
        toast.error(msg);
      },
    }
  );

  const globalOpsMutation = useMutation(
    ({ id, globalTeachingOps }) => adminUsersAPI.updateGlobalTeachingOps(id, globalTeachingOps),
    {
      onSuccess: () => {
        toast.success('Global teaching ops updated');
        queryClient.invalidateQueries('adminUsers');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to update flag');
      },
    }
  );

  const roleMutation = useMutation(
    ({ id, role }) => adminUsersAPI.updateRole(id, role),
    {
      onSuccess: () => {
        toast.success('Role updated');
        queryClient.invalidateQueries('adminUsers');
      },
      onError: (err) => {
        const msg = err.response?.data?.message || 'Failed to update role';
        toast.error(msg);
      },
    }
  );

  const resetMutation = useMutation(
    ({ id, newPassword }) => adminUsersAPI.resetPassword(id, newPassword),
    {
      onSuccess: (res) => {
        toast.success(res?.data?.message || 'Password reset');
        setResetOpen(false);
        setResetForm({ newPassword: '', confirm: '' });
        setSelectedUser(null);
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to reset password');
        toast.error(msg);
      },
    }
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? value : 1,
    }));
  };

  const handleOpenCreate = () => {
    setCreateForm({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      country: '',
      timezone: 'UTC',
      role: staffAssignableRoles[0] || 'teacher',
      globalTeachingOps: false,
      assignedCountries: [],
      demoReady: 'No',
      demoCountries: [],
    });
    setCreateOpen(true);
  };

  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      country: user.country || '',
      timezone: user.timezone || 'UTC',
      isActive: user.isActive !== false,
      role: user.role || '',
      globalTeachingOps: !!user.globalTeachingOps,
      assignedCountries: user.assignedCountries || [],
      demoReady: user.demoReady || 'No',
      demoCountries: user.demoCountries || [],
    });
    setEditOpen(true);
  };

  const handleOpenReset = (user) => {
    setSelectedUser(user);
    setResetForm({ newPassword: '', confirm: '' });
    setResetOpen(true);
  };

  const handleOpenEmail = (user) => {
    setSelectedUser(user);
    setEmailForm({ next: '', confirm: '' });
    setEmailStep('edit');
    setEmailError('');
    setEmailNotices([]);
    setEmailOpen(true);
  };

  const handleEmailContinue = async () => {
    if (!selectedUser?.id || emailBusy) return;
    const issue = clientLoginEmailIssue(selectedUser.email, emailForm.next, emailForm.confirm);
    if (issue) {
      setEmailError(issue);
      setEmailStep('edit');
      return;
    }
    setEmailBusy(true);
    setEmailError('');
    try {
      const res = await adminUsersAPI.previewEmailChange(selectedUser.id, emailForm.next.trim());
      setEmailNotices(res.data?.data?.notices || []);
      setEmailStep('confirm');
    } catch (err) {
      setEmailError(err.response?.data?.message || 'Could not check this email.');
      setEmailStep('edit');
    } finally {
      setEmailBusy(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!selectedUser?.id || emailBusy) return;
    setEmailBusy(true);
    setEmailError('');
    try {
      const res = await adminUsersAPI.changeEmail(selectedUser.id, emailForm.next.trim());
      const nextEmail = res.data?.data?.email || emailForm.next.trim().toLowerCase();
      if (actor?.id && String(actor.id) === String(selectedUser.id)) {
        updateUser({ ...actor, email: nextEmail });
      }
      toast.success(res.data?.message || 'Login email updated.');
      setEmailOpen(false);
      queryClient.invalidateQueries('adminUsers');
    } catch (err) {
      setEmailError(err.response?.data?.message || 'Could not change the login email.');
      setEmailStep('edit');
    } finally {
      setEmailBusy(false);
    }
  };

  const handleCreateSubmit = () => {
    if (!createForm.email || !createForm.password || !createForm.firstName || !createForm.lastName || !createForm.country || !createForm.role) {
      toast.error('Email, password, first name, last name, country, and role are required');
      return;
    }
    if (createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (FAMILY_ROLES_HIDDEN_FROM_ASSIGN.has(createForm.role)) {
      toast.error(STUDENT_ROLE_MESSAGE);
      return;
    }
    createMutation.mutate({
      email: createForm.email.trim(),
      password: createForm.password,
      firstName: createForm.firstName.trim(),
      lastName: createForm.lastName.trim(),
      phone: createForm.phone?.trim() || null,
      country: createForm.country.trim(),
      timezone: createForm.timezone || 'UTC',
      role: createForm.role,
      globalTeachingOps:
        createForm.role === 'teacher_supervisor' ? !!createForm.globalTeachingOps : false,
      assignedCountries: keepsAssignedCountries(createForm.role) ? createForm.assignedCountries : [],
      demoReady: keepsDemoProfile(createForm.role) ? createForm.demoReady : undefined,
      demoCountries: keepsDemoProfile(createForm.role) ? createForm.demoCountries : undefined,
    });
  };

  const handleEditSubmit = () => {
    if (!selectedUser?.id) return;
    updateMutation.mutate({
      id: selectedUser.id,
      payload: {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        phone: editForm.phone?.trim() || null,
        country: editForm.country.trim(),
        timezone: editForm.timezone || 'UTC',
        isActive: editForm.isActive,
        assignedCountries: keepsAssignedCountries(editForm.role) ? editForm.assignedCountries : [],
        ...(editForm.role === 'teacher_supervisor'
          ? { globalTeachingOps: !!editForm.globalTeachingOps }
          : { globalTeachingOps: false }),
        ...(keepsDemoProfile(editForm.role)
          ? {
              demoReady: editForm.demoReady || 'No',
              demoCountries: editForm.demoCountries || [],
            }
          : {}),
      },
    });
  };

  const handleRoleChange = (userId, role) => {
    if (FAMILY_ROLES_HIDDEN_FROM_ASSIGN.has(role)) {
      toast.error(STUDENT_ROLE_MESSAGE);
      return;
    }
    roleMutation.mutate({ id: userId, role });
  };

  const handleResetSubmit = () => {
    if (!selectedUser?.id) return;
    if (resetForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (resetForm.newPassword !== resetForm.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    resetMutation.mutate({ id: selectedUser.id, newPassword: resetForm.newPassword });
  };

  const rows = data?.data || [];
  const pagination = data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        User Management
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Create, edit, and assign roles to users. Change a login email or reset a password when needed.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              size="small"
              fullWidth
              label="Role"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {roles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              size="small"
              fullWidth
              label="Status"
              value={filters.isActive}
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small"
              fullWidth
              label="Search (email / name)"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            {canManageUsersFully ? (
              <Button variant="contained" onClick={handleOpenCreate} fullWidth>
                Create User
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>Global ops</span>
                    <Tooltip title="For teacher_supervisor only: keep team approvals, but allow view/create/edit/delete batches and demos/trials for all teachers.">
                      <IconButton size="small" aria-label="About global teaching ops">
                        <InfoOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Assigned countries</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8}>Loading...</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>No users found.</TableCell>
                </TableRow>
              ) : (
                rows.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim()}</TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={user.role === 'parent' ? '' : user.role}
                        onChange={(e) => {
                          if (!e.target.value) return;
                          handleRoleChange(user.id, e.target.value);
                        }}
                        sx={{ minWidth: 140 }}
                        disabled={!canManageUsersFully || roleMutation.isLoading}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => selected || '—',
                        }}
                      >
                        {user.role === 'parent' ? (
                          <MenuItem value="" sx={{ display: 'none' }} />
                        ) : null}
                        {roleOptionsForUser(user.role).map((r) => (
                          <MenuItem key={r} value={r} disabled={r === 'student'}>
                            {r}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {user.role === 'teacher_supervisor' ? (
                        <Checkbox
                          checked={!!user.globalTeachingOps}
                          disabled={globalOpsMutation.isLoading}
                          onChange={(e) =>
                            globalOpsMutation.mutate({
                              id: user.id,
                              globalTeachingOps: e.target.checked,
                            })
                          }
                          inputProps={{ 'aria-label': 'Global teaching ops' }}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{user.country || '-'}</TableCell>
                    <TableCell>
                      {user.assignedCountries?.length ? user.assignedCountries.join(', ') : '—'}
                    </TableCell>
                    <TableCell>{user.isActive !== false ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      {canManageUsersFully ? (
                        <>
                          <Button size="small" onClick={() => handleOpenEdit(user)} sx={{ mr: 1 }}>
                            Edit
                          </Button>
                          <Button size="small" onClick={() => handleOpenEmail(user)} sx={{ mr: 1 }}>
                            Change email
                          </Button>
                          <Button size="small" onClick={() => handleOpenReset(user)}>
                            Reset password
                          </Button>
                        </>
                      ) : user.role === 'teacher_supervisor' ? (
                        <Typography variant="caption" color="text.secondary">
                          Toggle Global ops
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={(pagination.page || 1) - 1}
          onPageChange={(_, p) => handleFilterChange('page', p + 1)}
          rowsPerPage={filters.limit}
          onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10) || 20)}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Create user dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))}
                required
                helperText="Min 8 characters, upper + lower + number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="First name"
                value={createForm.firstName}
                onChange={(e) => setCreateForm((p) => ({ ...p, firstName: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Last name"
                value={createForm.lastName}
                onChange={(e) => setCreateForm((p) => ({ ...p, lastName: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                value={createForm.phone}
                onChange={(e) => setCreateForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Country"
                value={createForm.country}
                onChange={(e) => setCreateForm((p) => ({ ...p, country: e.target.value }))}
                required
              >
                <MenuItem value="">Select</MenuItem>
                {countryList.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Timezone"
                value={createForm.timezone}
                onChange={(e) => setCreateForm((p) => ({ ...p, timezone: e.target.value }))}
              >
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {TIMEZONE_LABELS[tz] || tz}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Role"
                value={createForm.role}
                onChange={(e) => setCreateForm((p) => ({ ...p, role: e.target.value }))}
              >
                {staffAssignableRoles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Students and parents cannot be created here. Add a student from the Students menu (the parent login is created with the student).
              </Typography>
              {createForm.role === 'teacher_supervisor' || createForm.role === 'teacher' ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  A teacher profile is created automatically so this user can have batches and sessions.
                </Typography>
              ) : null}
              {createForm.role === 'sales' ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  A sales profile is created automatically so this user appears in Sales Users.
                </Typography>
              ) : null}
              {keepsAssignedCountries(createForm.role) ? (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Assigned countries"
                  value={createForm.assignedCountries}
                  onChange={(e) => setCreateForm((p) => ({ ...p, assignedCountries: e.target.value }))}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      selected?.length ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {selected.map((value) => (
                            <Chip key={value} size="small" label={value} />
                          ))}
                        </Stack>
                      ) : (
                        'Select countries'
                      ),
                  }}
                  sx={{ mt: 1 }}
                  helperText={
                    createForm.role === 'sales'
                      ? 'Leads, WhatsApp chats, and new ad leads use this list. Leave it empty to use Country above. Demo countries stay separate.'
                      : createForm.role === 'sales_supervisor' || createForm.role === 'country_manager'
                        ? 'Leads, WhatsApp chats, students, and batches for these countries. New ad leads are assigned from this list. At least one country is required.'
                        : 'This user can only view students and monthly reports for these countries.'
                  }
                >
                  {countryList.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              {keepsDemoProfile(createForm.role) ? (
                <>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Demo ready"
                    value={createForm.demoReady}
                    onChange={(e) => setCreateForm((p) => ({ ...p, demoReady: e.target.value }))}
                    sx={{ mt: 1 }}
                    helperText="Yes = this person can be picked as the demo instructor"
                  >
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Demo countries"
                    value={createForm.demoCountries}
                    onChange={(e) => setCreateForm((p) => ({ ...p, demoCountries: e.target.value }))}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) =>
                        selected?.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {selected.map((value) => (
                              <Chip key={value} size="small" label={value} />
                            ))}
                          </Stack>
                        ) : (
                          'Select countries'
                        ),
                    }}
                    sx={{ mt: 1 }}
                  >
                    {countryList.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              ) : null}
              {createForm.role === 'teacher_supervisor' ? (
                <FormControlLabel
                  sx={{ mt: 1, alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      checked={!!createForm.globalTeachingOps}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, globalTeachingOps: e.target.checked }))
                      }
                    />
                  }
                  label={
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2">Global teaching ops</Typography>
                        <Tooltip title="Keep team approvals; also allow batches and demos/trials for all teachers.">
                          <InfoOutlinedIcon fontSize="inherit" color="action" />
                        </Tooltip>
                      </Stack>
                      <FormHelperText sx={{ m: 0 }}>
                        Org-wide batch / demo / trial create-edit-delete
                      </FormHelperText>
                    </Box>
                  }
                />
              ) : null}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={createMutation.isLoading}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User {selectedUser?.email}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Role can be changed from the table. Here you can update profile and active status.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="First name"
                value={editForm.firstName}
                onChange={(e) => setEditForm((p) => ({ ...p, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Last name"
                value={editForm.lastName}
                onChange={(e) => setEditForm((p) => ({ ...p, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                value={editForm.phone}
                onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Country"
                value={editForm.country}
                onChange={(e) => setEditForm((p) => ({ ...p, country: e.target.value }))}
              >
                <MenuItem value="">Select</MenuItem>
                {countryList.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Timezone"
                value={editForm.timezone}
                onChange={(e) => setEditForm((p) => ({ ...p, timezone: e.target.value }))}
              >
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {TIMEZONE_LABELS[tz] || tz}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            {editForm.role === 'teacher_supervisor' ? (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!editForm.globalTeachingOps}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, globalTeachingOps: e.target.checked }))
                      }
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>Global teaching ops</span>
                      <Tooltip title="Keep team approvals; also allow batches and demos/trials for all teachers.">
                        <InfoOutlinedIcon fontSize="inherit" color="action" />
                      </Tooltip>
                    </Stack>
                  }
                />
              </Grid>
            ) : null}
            {keepsAssignedCountries(editForm.role) ? (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Assigned countries"
                  value={editForm.assignedCountries}
                  onChange={(e) => setEditForm((p) => ({ ...p, assignedCountries: e.target.value }))}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      selected?.length ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {selected.map((value) => (
                            <Chip key={value} size="small" label={value} />
                          ))}
                        </Stack>
                      ) : (
                        'Select countries'
                      ),
                  }}
                  helperText={
                    editForm.role === 'sales'
                      ? 'Leads, WhatsApp chats, and new ad leads use this list. Leave it empty to use Country above. Demo countries stay separate.'
                      : editForm.role === 'sales_supervisor' || editForm.role === 'country_manager'
                        ? 'Leads, WhatsApp chats, students, and batches for these countries. New ad leads are assigned from this list. At least one country is required.'
                        : 'This user can only view students and monthly reports for these countries.'
                  }
                >
                  {countryList.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
            {keepsDemoProfile(editForm.role) ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Demo ready"
                    value={editForm.demoReady}
                    onChange={(e) => setEditForm((p) => ({ ...p, demoReady: e.target.value }))}
                    helperText="Yes = this person can be picked as the demo instructor"
                  >
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Demo countries"
                    value={editForm.demoCountries}
                    onChange={(e) => setEditForm((p) => ({ ...p, demoCountries: e.target.value }))}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) =>
                        selected?.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {selected.map((value) => (
                              <Chip key={value} size="small" label={value} />
                            ))}
                          </Stack>
                        ) : (
                          'Select countries'
                        ),
                    }}
                  >
                    {countryList.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={updateMutation.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailOpen} onClose={() => !emailBusy && setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change login email</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current login email: {selectedUser?.email}
          </Typography>
          {emailError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emailError}
            </Alert>
          ) : null}
          {emailStep === 'confirm' ? (
            <Box>
              {emailNotices.map((notice, index) => (
                <Typography key={`${index}-${notice}`} variant="body2" sx={{ mb: 1.5 }}>
                  {notice}
                </Typography>
              ))}
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="New login email"
                  type="email"
                  value={emailForm.next}
                  onChange={(e) => setEmailForm((p) => ({ ...p, next: e.target.value }))}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Type the new email again"
                  type="email"
                  value={emailForm.confirm}
                  onChange={(e) => setEmailForm((p) => ({ ...p, confirm: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)} disabled={emailBusy}>
            Cancel
          </Button>
          {emailStep === 'confirm' ? (
            <>
              <Button onClick={() => { setEmailStep('edit'); setEmailError(''); }} disabled={emailBusy}>
                Back
              </Button>
              <Button variant="contained" onClick={handleEmailSubmit} disabled={emailBusy}>
                Change login email
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleEmailContinue} disabled={emailBusy}>
              Continue
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password – {selectedUser?.email}</DialogTitle>
        <DialogContent dividers>
          <FormHelperText sx={{ mb: 1, mx: 0 }}>
            For a family account (children linked), share this password with the parent. They sign in at the usual login page (web or the BacchaPanti app).
          </FormHelperText>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="New password"
                type="password"
                value={resetForm.newPassword}
                onChange={(e) => setResetForm((p) => ({ ...p, newPassword: e.target.value }))}
                helperText="Min 8 characters, upper + lower + number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Confirm password"
                type="password"
                value={resetForm.confirm}
                onChange={(e) => setResetForm((p) => ({ ...p, confirm: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResetSubmit}
            disabled={resetMutation.isLoading || !resetForm.newPassword || resetForm.newPassword !== resetForm.confirm}
          >
            Reset password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
